
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Facebook, Share2, Send, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  compact?: boolean;
}

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.88 11.94L4 20l4.2-1.1a7.83 7.83 0 0 0 3.85 1 8 8 0 0 0 8-7.93 8 8 0 0 0-2.45-5.65zM12.05 18.29a6.55 6.55 0 0 1-3.36-.92l-.24-.15-2.5.66.66-2.42-.15-.24a6.59 6.59 0 0 1 10.07-8.2 6.59 6.59 0 0 1 0 9.32 6.6 6.6 0 0 1-4.48 1.95zm3.93-4.93c-.2-.1-1.2-.59-1.4-.66s-.32-.1-.46.1-.53.66-.65.8-.25.14-.44.05a5.57 5.57 0 0 1-1.64-1.01 6.18 6.18 0 0 1-1.15-1.43c-.12-.21 0-.32.09-.42s.2-.24.3-.37a1.34 1.34 0 0 0 .14-.23.26.26 0 0 0 0-.24c0-.1-.46-1.12-.63-1.53s-.34-.35-.46-.35h-.4a.77.77 0 0 0-.56.25A2.36 2.36 0 0 0 8 9.79a4.17 4.17 0 0 0 .85 2.2 9.44 9.44 0 0 0 3.6 3.19 12.01 12.01 0 0 0 1.2.45 2.84 2.84 0 0 0 1.34.08 2.17 2.17 0 0 0 1.42-1 1.82 1.82 0 0 0 .12-1 .76.76 0 0 0-.34-.26z"
      fill="#25D366"
    />
  </svg>
);

const SocialShare = ({ url, title, description, compact = false }: SocialShareProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isMobile = useIsMobile();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = description ? encodeURIComponent(description) : '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    }
  };

  // Use compact layout for mobile or when explicitly requested
  if (isMobile || compact) {
    return (
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
          >
            <Share2 size={14} className="mr-1" />
            Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => window.open(shareLinks.facebook, '_blank')}>
            <Facebook size={14} className="mr-2 text-[#1877F2]" />
            Facebook
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(shareLinks.x, '_blank')}>
            <XIcon />
            <span className="ml-2">X</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(shareLinks.whatsapp, '_blank')}>
            <WhatsAppIcon />
            <span className="ml-2">WhatsApp</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => window.open(shareLinks.telegram, '_blank')}>
            <Send size={14} className="mr-2 text-[#0088cc]" />
            Telegram
          </DropdownMenuItem>
          {navigator.share && (
            <DropdownMenuItem onClick={handleShare}>
              <Share2 size={14} className="mr-2" />
              More Options
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Desktop/Tablet layout - show individual buttons
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
        onClick={() => window.open(shareLinks.facebook, '_blank')}
      >
        <Facebook size={14} className="mr-1 text-[#1877F2]" />
        <span className="hidden sm:inline">Facebook</span>
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        className="text-xs hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        onClick={() => window.open(shareLinks.x, '_blank')}
      >
        <XIcon />
        <span className="hidden sm:inline ml-1">X</span>
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        className="text-xs hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
        onClick={() => window.open(shareLinks.whatsapp, '_blank')}
      >
        <WhatsAppIcon />
        <span className="hidden sm:inline ml-1">WhatsApp</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        className="text-xs hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
        onClick={() => window.open(shareLinks.telegram, '_blank')}
      >
        <Send size={14} className="mr-1 text-[#0088cc]" />
        <span className="hidden sm:inline">Telegram</span>
      </Button>

      {navigator.share && (
        <Button
          variant="outline"
          size="sm"
          className="text-xs hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          onClick={handleShare}
        >
          <Share2 size={14} className="mr-1" />
          <span className="hidden sm:inline">More</span>
        </Button>
      )}
    </div>
  );
};

export default SocialShare;
